// @generated by liquid-inject from src/blocks/_algolia-recommend-list/_algolia-recommend-list.ts
// src/blocks/_algolia-recommend-list/shared.ts
import {
  ALGOLIA_HIDDEN_GUARD_CONTEXT,
  ALGOLIA_HIDDEN_PRODUCTS_FILTER,
  ALGOLIA_PRODUCT_RESPONSE_FIELDS,
  productAttributesToRetrieve,
  hasGiftInCart,
  refreshGiftedHandles,
  renderCards,
  resolveGiftsForHits
} from "@theme/algolia-shared";

// src/shared/card-product/align-product-card-zones.ts
var CARD_SELECTOR = ".card-product:not(.set-to-compose)";
var ZONE_CONFIGS = [
  { selector: ".card-product__meta", cssVar: "--card-meta-min-height" },
  { selector: ".card-product__price", cssVar: "--card-price-min-height" }
];
function groupIndicesByRow(tops) {
  const rows = [];
  for (const [index, top] of tops.entries()) {
    const row = rows.find((group) => {
      const first = group[0];
      return first !== void 0 && Math.abs((tops[first] ?? 0) - top) <= 1;
    });
    if (row) {
      row.push(index);
    } else {
      rows.push([index]);
    }
  }
  return rows;
}
function alignProductCardZones(root) {
  const cards = Array.from(root.querySelectorAll(CARD_SELECTOR));
  if (cards.length === 0) return;
  const zones = ZONE_CONFIGS.map(
    ({ selector }) => cards.map((card) => card.querySelector(selector))
  );
  for (const [zoneIndex, elements] of zones.entries()) {
    const cssVar = ZONE_CONFIGS[zoneIndex]?.cssVar;
    if (cssVar === void 0) continue;
    for (const el of elements) {
      el?.style.setProperty(cssVar, "0px");
    }
  }
  const tops = cards.map((card) => card.offsetTop);
  const heights = zones.map(
    (elements) => elements.map((el) => el?.scrollHeight ?? 0)
  );
  const rows = groupIndicesByRow(tops);
  for (const [zoneIndex, elements] of zones.entries()) {
    const cssVar = ZONE_CONFIGS[zoneIndex]?.cssVar;
    const zoneHeights = heights[zoneIndex];
    if (cssVar === void 0 || zoneHeights === void 0) continue;
    for (const row of rows) {
      let maxHeight = 0;
      for (const index of row) {
        maxHeight = Math.max(maxHeight, zoneHeights[index] ?? 0);
      }
      const value = `${String(maxHeight)}px`;
      for (const index of row) {
        elements[index]?.style.setProperty(cssVar, value);
      }
    }
  }
}

// src/shared/card-product/predecode-hover-visuals.ts
var HOVER_IMAGE_SELECTOR = ".card-product_image.is-hover-active figure:nth-child(even) img";
var NEAR_VIEWPORT_MARGIN = "400px";
var seen = /* @__PURE__ */ new WeakSet();
var observer = null;
function whenIdle(task) {
  if (typeof requestIdleCallback === "function") {
    requestIdleCallback(task, { timeout: 2e3 });
    return;
  }
  window.setTimeout(task, 200);
}
function decodeWhenLoaded(image) {
  const decode = () => whenIdle(() => void image.decode().catch(() => {
  }));
  if (image.complete && image.naturalWidth > 0) {
    decode();
    return;
  }
  image.addEventListener("load", decode, { once: true });
}
function nearViewportObserver() {
  if (observer) return observer;
  if (typeof IntersectionObserver !== "function") return null;
  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer?.unobserve(entry.target);
        decodeWhenLoaded(entry.target);
      }
    },
    { rootMargin: NEAR_VIEWPORT_MARGIN }
  );
  return observer;
}
function predecodeHoverVisuals(root) {
  const images = root.querySelectorAll(HOVER_IMAGE_SELECTOR);
  if (images.length === 0) return;
  const io = nearViewportObserver();
  for (const image of images) {
    if (seen.has(image)) continue;
    seen.add(image);
    if (!io) {
      decodeWhenLoaded(image);
      continue;
    }
    io.observe(image);
  }
}

// src/shared/gallery_list/gallery_list.ts
var GalleryListBase = class extends HTMLElement {
  constructor() {
    super(...arguments);
    this.embla = null;
    this.sliderToScrollMobile = "auto";
    this.sliderToScrollDesktop = "auto";
    this.parentContainer = null;
    this.viewport = null;
    this.container = null;
    this.prevButton = null;
    this.nextButton = null;
    this.progressNode = null;
    this.prevContainer = null;
    this.nextContainer = null;
    this.firstSlideObserver = null;
    this.zoneAlignObserver = null;
    this.isAligningZones = false;
    /** Dernière largeur alignée, pour ignorer les resizes en hauteur. */
    this.lastAlignedWidth = 0;
    this.onPrevClick = () => this.embla?.scrollPrev();
    this.onNextClick = () => this.embla?.scrollNext();
    this.onPrevMouseMove = (e) => this.followMouse(e, this.prevButton);
    this.onNextMouseMove = (e) => this.followMouse(e, this.nextButton);
    this.onPrevMouseLeave = () => this.resetButton(this.prevButton);
    this.onNextMouseLeave = () => this.resetButton(this.nextButton);
    /**
     * Re-equalizes card text zones; skips while a sync is already in flight.
     *
     * Ne touche PAS au `ResizeObserver` : le déconnecter ici puis le ré-`observe()`
     * en sortie relançait l'alignement à chaque frame, sans fin — `observe()` remet
     * `lastReportedSize` à zéro, donc la notification suivante était garantie. Voir
     * la garde en largeur de `bindZoneAlignment`.
     */
    this.alignZones = () => {
      if (!this.container || this.isAligningZones) return;
      this.isAligningZones = true;
      try {
        alignProductCardZones(this.container);
        predecodeHoverVisuals(this.container);
      } finally {
        this.isAligningZones = false;
      }
    };
  }
  followMouse(e, button) {
    if (!button) return;
    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();
    const offsetX = e.clientX - rect.left - rect.width / 2;
    const offsetY = e.clientY - rect.top - rect.height / 2;
    button.style.transform = `translate(${offsetX}px, ${offsetY}px)`;
  }
  resetButton(button) {
    if (!button) return;
    button.style.transform = "";
  }
  // Hook for subclasses to react to scroll events
  onScroll() {
  }
  connectedCallback() {
    this.parentContainer = this.closest("c-gallery-container");
    this.viewport = this.querySelector(".embla__viewport");
    this.container = this.querySelector(".embla__container");
    this.prevButton = this.querySelector(".embla__prev");
    this.nextButton = this.querySelector(".embla__next");
    this.progressNode = this.querySelector(".embla__progress__bar");
    this.prevContainer = this.querySelector(".container_embla__prev");
    this.nextContainer = this.querySelector(".container_embla__next");
    this.sliderToScrollMobile = this.dataset.slidesToScrollMobile || "auto";
    this.sliderToScrollDesktop = this.dataset.slidesToScrollDesktop || "auto";
    if (this.parentContainer?.classList.contains("v-slider")) {
      this.initEmbla();
      this.bindProgress();
      this.bindMouseFollow();
    }
    this.classList.remove("shimmer");
    this.bindFirstSlideAnimation();
    this.bindZoneAlignment();
  }
  /**
   * Keeps meta / price aligned across cards in a row.
   * Runs on connect, Embla reinit/resize, and container width changes. Does not
   * call embla.reInit() to avoid AutoHeight loops.
   *
   * Garde en LARGEUR : l'alignement ne dépend que de la largeur disponible et ne
   * produit que des changements de HAUTEUR. Réagir à la hauteur revenait à
   * réagir à son propre effet. Le chargement des polices, que l'observation
   * rattrapait par accident, est accroché explicitement plus bas.
   */
  bindZoneAlignment() {
    const container = this.container;
    if (!container) return;
    this.alignZones();
    this.zoneAlignObserver = new ResizeObserver((entries) => {
      const width = Math.round(entries[0]?.contentRect.width ?? 0);
      if (width === 0 || width === this.lastAlignedWidth) return;
      this.lastAlignedWidth = width;
      this.alignZones();
    });
    this.lastAlignedWidth = Math.round(container.getBoundingClientRect().width);
    this.zoneAlignObserver.observe(container);
    void document.fonts.ready.then(() => this.alignZones());
    this.embla?.on("reinit", () => this.alignZones()).on("resize", () => this.alignZones());
  }
  bindFirstSlideAnimation() {
    const firstSlide = this.querySelector(
      ".embla__slide:first-child"
    );
    if (!firstSlide) return;
    this.firstSlideObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry.isIntersecting) return;
        this.firstSlideObserver?.disconnect();
        this.firstSlideObserver = null;
        const quickBuyContainer = firstSlide.querySelector(
          ".card-product-quick-buy__container"
        );
        if (!quickBuyContainer) return;
        quickBuyContainer.classList.add("is-animate-quick-buy");
        setTimeout(
          () => quickBuyContainer.classList.remove("is-animate-quick-buy"),
          1400
        );
      },
      { threshold: 0.8 }
    );
    this.firstSlideObserver.observe(firstSlide);
  }
  bindMouseFollow() {
    this.prevContainer?.addEventListener("mousemove", this.onPrevMouseMove);
    this.prevContainer?.addEventListener("mouseleave", this.onPrevMouseLeave);
    this.nextContainer?.addEventListener("mousemove", this.onNextMouseMove);
    this.nextContainer?.addEventListener("mouseleave", this.onNextMouseLeave);
  }
  initEmbla() {
    if (!this.viewport || !this.container) return;
    this.embla = EmblaCarousel(
      this.viewport,
      {
        dragFree: false,
        active: true,
        slidesToScroll: this.sliderToScrollMobile,
        "(min-width: 1024px)": {
          active: false,
          dragFree: true,
          slidesToScroll: this.sliderToScrollDesktop
        }
      },
      [EmblaCarouselAutoHeight()]
    );
    this.prevButton?.addEventListener("click", this.onPrevClick);
    this.nextButton?.addEventListener("click", this.onNextClick);
  }
  bindProgress() {
    if (!this.embla || !this.progressNode) return;
    const progressNode = this.progressNode;
    const applyProgress = () => {
      if (!this.embla) return;
      const snapCount = this.embla.scrollSnapList().length;
      const selected = this.embla.selectedScrollSnap();
      progressNode.style.width = `${100 / snapCount}%`;
      progressNode.style.transform = `translate3d(${selected * 100}%,0px,0px)`;
      this.prevButton?.parentElement?.toggleAttribute(
        "data-disabled",
        !this.embla.canScrollPrev()
      );
      this.nextButton?.parentElement?.toggleAttribute(
        "data-disabled",
        !this.embla.canScrollNext()
      );
      this.onScroll();
    };
    applyProgress();
    this.embla.on("reinit", () => applyProgress()).on("resize", () => applyProgress()).on("scroll", () => applyProgress()).on("slidefocus", () => applyProgress());
  }
  disconnectedCallback() {
    this.zoneAlignObserver?.disconnect();
    this.zoneAlignObserver = null;
    this.lastAlignedWidth = 0;
    this.embla?.destroy();
    this.embla = null;
    this.firstSlideObserver?.disconnect();
    this.firstSlideObserver = null;
    this.prevButton?.removeEventListener("click", this.onPrevClick);
    this.nextButton?.removeEventListener("click", this.onNextClick);
    this.prevContainer?.removeEventListener("mousemove", this.onPrevMouseMove);
    this.prevContainer?.removeEventListener("mouseleave", this.onPrevMouseLeave);
    this.nextContainer?.removeEventListener("mousemove", this.onNextMouseMove);
    this.nextContainer?.removeEventListener("mouseleave", this.onNextMouseLeave);
  }
};

// src/shared/utils/section-render.ts
var SECTION_PREFIX = "shopify-section-";
function getSectionId(el) {
  if (!el?.id?.startsWith(SECTION_PREFIX)) return null;
  return el.id.slice(SECTION_PREFIX.length);
}
function closestSectionId(el) {
  const wrapper = el?.closest(`[id^="${SECTION_PREFIX}"]`);
  return getSectionId(wrapper);
}
function collectSectionIds(elements) {
  const ids = /* @__PURE__ */ new Set();
  for (const el of elements) {
    const id = closestSectionId(el);
    if (id) ids.add(id);
  }
  return [...ids];
}
function buildUrl(base, params) {
  const url = new URL(base ?? window.location.pathname, window.location.origin);
  for (const [key, value] of Object.entries(params)) {
    url.searchParams.set(key, value);
  }
  return url.toString();
}
function withQuery(params, query) {
  if (!query) return params;
  const extra = Object.fromEntries(
    Object.entries(query).map(([k, v]) => [k, String(v)])
  );
  return { ...extra, ...params };
}
async function fetchSections(ids, options = {}) {
  const url = buildUrl(
    options.url,
    withQuery({ sections: ids.join(",") }, options.query)
  );
  const response = await fetch(url, { credentials: "same-origin" });
  if (!response.ok) throw new Error(`Sections fetch ${response.status}`);
  return response.json();
}
async function fetchSectionHTML(id, options = {}) {
  const url = buildUrl(
    options.url,
    withQuery({ section_id: id }, options.query)
  );
  const response = await fetch(url, { credentials: "same-origin" });
  if (!response.ok) throw new Error(`Section fetch ${response.status}`);
  return response.text();
}
function swapInner(html, selector) {
  const selectors = Array.isArray(selector) ? selector : [selector];
  const doc = new DOMParser().parseFromString(html, "text/html");
  selectors.forEach((sel) => {
    document.querySelectorAll(sel).forEach((element) => {
      const source = element.dataset.id ? doc.querySelector(`${sel}[data-id="${element.dataset.id}"]`) : doc.querySelector(sel);
      if (source) element.innerHTML = source.innerHTML;
    });
  });
}
function swapSection(id, html) {
  const live = document.getElementById(`${SECTION_PREFIX}${id}`);
  if (!live) return;
  const doc = new DOMParser().parseFromString(html, "text/html");
  const next = doc.getElementById(`${SECTION_PREFIX}${id}`) || doc.body.firstElementChild;
  if (next instanceof HTMLElement) {
    live.replaceWith(next);
  } else {
    live.hidden = true;
  }
}
async function renderSections(targets, options = {}) {
  if (targets.length === 0) return;
  const map = options.sections ?? await fetchSections(
    targets.map((t) => t.id),
    options
  );
  for (const target of targets) {
    const html = map[target.id];
    if (typeof html !== "string") continue;
    if (target.selector) {
      swapInner(html, target.selector);
    } else {
      swapSection(target.id, html);
    }
  }
}
async function renderSection(target, options = {}) {
  const html = await fetchSectionHTML(target.id, options);
  if (target.selector) {
    swapInner(html, target.selector);
  } else {
    swapSection(target.id, html);
  }
}

// src/shared/cart-limit-error/cart-limit-error.ts
var ERROR_MESSAGE_SELECTOR = "[data-cart-limit-error-message]";
var ERROR_TEXT_SELECTOR = "[data-cart-limit-error-text]";
var OPEN_ENGRAVING_PANEL_SELECTOR = "c-engraving-panel.is-open";
var SEARCH_DIALOG_SELECTOR = "dialog[data-search-dialog]";
var QUANTITY_LIMIT_PATTERNS = [
  /can'?t add more/i,
  /maximum allowed quantity/i,
  /maximum quantity/i,
  /quantity.*exceeded/i,
  /only add \d+/i
];
function getErrorMessageEl() {
  return document.querySelector(ERROR_MESSAGE_SELECTOR);
}
function getDefaultMessage() {
  return window.cartStrings?.addToCartLimitError ?? "The maximum allowed quantity for this product has been exceeded.";
}
function isCartQuantityLimitError(status, description) {
  if (status !== 422) return false;
  const normalized = description.trim();
  if (normalized.length === 0) return true;
  return QUANTITY_LIMIT_PATTERNS.some((pattern) => pattern.test(normalized));
}
function closeOpenEngravingPanels() {
  document.querySelectorAll(OPEN_ENGRAVING_PANEL_SELECTOR).forEach((panel) => {
    ;
    panel.close();
  });
}
function closeHeaderSearchIfOpen() {
  const openDialogs = document.querySelectorAll(
    `${SEARCH_DIALOG_SELECTOR}[open]`
  );
  if (openDialogs.length === 0) return;
  document.documentElement.classList.remove("is-overflow-hidden");
  openDialogs.forEach((dialog) => {
    dialog.close();
  });
}
function showCartLimitError(message) {
  closeHeaderSearchIfOpen();
  const errorMessage = getErrorMessageEl();
  if (!errorMessage) return;
  const textEl = errorMessage.querySelector(ERROR_TEXT_SELECTOR);
  if (textEl) {
    textEl.textContent = message ?? getDefaultMessage();
  }
  errorMessage.classList.remove("hidden");
  errorMessage.removeAttribute("hidden");
  errorMessage.setAttribute("aria-hidden", "false");
  closeOpenEngravingPanels();
  window.scrollTo(0, 0);
}
function hideCartLimitError() {
  const errorMessage = getErrorMessageEl();
  if (!errorMessage) return;
  const textEl = errorMessage.querySelector(ERROR_TEXT_SELECTOR);
  if (textEl) {
    textEl.textContent = "";
  }
  errorMessage.classList.add("hidden");
  errorMessage.setAttribute("hidden", "");
  errorMessage.setAttribute("aria-hidden", "true");
}

// src/shared/cart-a11y/cart-a11y.ts
var CART_LIVE_STATUS_ID = "cart-live-status";
var FOCUS_ANNOUNCE_DELAY_MS = 300;
function getOrCreateCartLiveRegion() {
  let region = document.getElementById(CART_LIVE_STATUS_ID);
  if (region instanceof HTMLElement) {
    return region;
  }
  region = document.createElement("div");
  region.id = CART_LIVE_STATUS_ID;
  region.className = "sr-only form-status";
  region.setAttribute("role", "status");
  region.setAttribute("aria-live", "polite");
  region.setAttribute("aria-atomic", "true");
  document.body.appendChild(region);
  return region;
}
function announceCartStatus(message, options = {}) {
  const trimmed = message.trim();
  if (!trimmed) return;
  const shouldFocus = options.focus !== false;
  const region = getOrCreateCartLiveRegion();
  if (shouldFocus) {
    region.setAttribute("aria-live", "off");
    region.setAttribute("tabindex", "-1");
    region.textContent = trimmed;
    region.focus({ preventScroll: true });
    window.setTimeout(() => {
      region.setAttribute("aria-live", "polite");
    }, FOCUS_ANNOUNCE_DELAY_MS);
    return;
  }
  region.textContent = "";
  requestAnimationFrame(() => {
    region.textContent = trimmed;
  });
}
function fillCartStatusTemplate(template, values) {
  let result = template;
  if (values.title !== void 0) {
    result = result.replace(/__TITLE__/g, values.title);
  }
  if (values.quantity !== void 0) {
    result = result.replace(/__QUANTITY__/g, String(values.quantity));
  }
  return result;
}

// src/shared/insights/algolia-insights.ts
var ENDPOINT = "https://insights.algolia.io/1/events";
var AGENT = "diptyque-theme";
var TOKEN_KEY = "algolia_user_token";
var PIXEL_CONFIG_KEY = "algolia_config";
var PIXEL_CLICK_KEY = "algolia_analytics_clicked_objects";
var PIXEL_CLICK_MAX = 50;
var MAX_OBJECT_IDS = 20;
var THEME_EMITS_ADD_TO_CART = true;
var OBJECT_ID_SELECTOR = "[data-algolia-object-id]";
var CLICK_HOOKS = "[data-algolia-click], [data-algolia-convert]";
function readConfig() {
  const data = document.body?.dataset;
  if (!data?.algoliaAppId || !data.algoliaApiKey || !data.algoliaIndex) {
    return null;
  }
  return {
    appId: data.algoliaAppId,
    apiKey: data.algoliaApiKey,
    index: data.algoliaIndex
  };
}
function readStorage(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeStorage(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
  }
}
function randomUserToken() {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return `anonymous-${Array.from(
    bytes,
    (byte) => byte.toString(36).padStart(2, "0")
  ).join("")}`;
}
var cachedUserToken = null;
function userToken() {
  if (cachedUserToken) return cachedUserToken;
  const stored = readStorage(TOKEN_KEY);
  cachedUserToken = stored || randomUserToken();
  if (!stored) writeStorage(TOKEN_KEY, cachedUserToken);
  return cachedUserToken;
}
function authenticatedUserToken() {
  return document.body?.dataset.algoliaAuthenticatedUserToken || void 0;
}
function normalizeObjectIDs(values) {
  const ids = /* @__PURE__ */ new Set();
  for (const value of values) {
    const id = String(value ?? "").trim();
    if (id) ids.add(id);
  }
  return Array.from(ids).slice(0, MAX_OBJECT_IDS);
}
function send(config, event) {
  const params = new URLSearchParams({
    "X-Algolia-Application-Id": config.appId,
    "X-Algolia-API-Key": config.apiKey,
    "X-Algolia-Agent": AGENT
  });
  const url = `${ENDPOINT}?${params}`;
  const body = JSON.stringify({ events: [event] });
  if (navigator.sendBeacon?.(url, body)) return;
  void fetch(url, { method: "POST", body, keepalive: true }).catch(() => {
  });
}
function track(config, objectIDs, event) {
  if (objectIDs.length === 0) return;
  send(config, {
    ...event,
    index: config.index,
    objectIDs,
    userToken: userToken(),
    authenticatedUserToken: authenticatedUserToken()
  });
}
function writePixelConfig(config) {
  writeStorage(
    PIXEL_CONFIG_KEY,
    JSON.stringify({
      app_id: config.appId,
      search_api_key: config.apiKey,
      index_name: config.index,
      user_token: userToken()
    })
  );
}
function recordClickForPixel(config, objectIDs, productId) {
  const stored = readStorage(PIXEL_CLICK_KEY);
  let records = [];
  try {
    const parsed = stored ? JSON.parse(stored) : [];
    if (Array.isArray(parsed)) records = parsed;
  } catch {
    records = [];
  }
  records.push({ index: config.index, objectIDs, productId });
  writeStorage(PIXEL_CLICK_KEY, JSON.stringify(records.slice(-PIXEL_CLICK_MAX)));
}
function readObjectId(element) {
  const above = element?.closest(OBJECT_ID_SELECTOR);
  if (above?.dataset.algoliaObjectId) return above.dataset.algoliaObjectId;
  const inside = element?.querySelector(OBJECT_ID_SELECTOR);
  return inside?.dataset.algoliaObjectId ?? "";
}
function readProductId(element) {
  const above = element?.closest("[data-algolia-product-id]");
  if (above?.dataset.algoliaProductId) return above.dataset.algoliaProductId;
  const inside = element?.querySelector(
    "[data-algolia-product-id]"
  );
  return inside?.dataset.algoliaProductId ?? "";
}
function onDocumentClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const hook = target.closest(CLICK_HOOKS);
  if (!hook) return;
  const config = readConfig();
  if (!config) return;
  const objectIDs = normalizeObjectIDs([readObjectId(hook)]);
  if (objectIDs.length === 0) return;
  writePixelConfig(config);
  recordClickForPixel(config, objectIDs, readProductId(hook));
  if (hook.matches("[data-algolia-convert]")) return;
  track(config, objectIDs, { eventType: "click", eventName: "Product Clicked" });
}
function onCartAddSuccess(event) {
  if (!THEME_EMITS_ADD_TO_CART) return;
  const target = event.target;
  if (!(target instanceof Element)) return;
  const config = readConfig();
  if (!config) return;
  const objectIDs = normalizeObjectIDs([readObjectId(target)]);
  if (objectIDs.length === 0) return;
  writePixelConfig(config);
  track(config, objectIDs, {
    eventType: "conversion",
    eventSubtype: "addToCart",
    eventName: "Add To Cart"
  });
}
function initAlgoliaInsights() {
  if (typeof window === "undefined" || window.algoliaInsightsReady) return;
  window.algoliaInsightsReady = true;
  document.addEventListener("click", onDocumentClick, true);
  document.addEventListener("cart:add-success", onCartAddSuccess);
}

// src/snippets/product-form/product-form.ts
var TECHNICAL_ERROR_PATTERN = /parameter/i;
var STOCK_LIMIT_PATTERN = /due to availability/i;
var DEFAULT_CART_ERROR = "There was an error while updating your cart. Please try again.";
var CartRequestError = class extends Error {
  constructor(status, technical, lineKeys, message) {
    super(message);
    this.status = status;
    this.technical = technical;
    this.lineKeys = lineKeys;
  }
};
function namedLineKeys(formData) {
  return [...formData.keys()].flatMap(
    (name) => /^updates\[(.+)\]$/.exec(name)?.[1] ?? []
  );
}
var CProductForm = class extends HTMLElement {
  constructor() {
    super(...arguments);
    this.SELECTORS = {
      form: "form",
      submitButton: 'button[type="submit"]',
      updatesInput: 'input[name^="updates["]',
      // Champ `updates[…]` que les boutons `+` / `−` ne touchent pas : la ligne
      // cadeau, dont la quantité est fixée à 1 par le Liquid.
      qtyFixedInput: "[data-qty-fixed]",
      qtyButton: "[data-qty-btn]",
      notifyMeButton: "[data-notify-me-button]",
      notifyWidget: '.gw-float-widget[type="button"]',
      loaderId: "full-page-loader",
      cartRecommendRoot: "[data-cart-recommend-root]",
      // Section `cart-samples` : ancre stable du wrapper + les deux blocs internes.
      samplesSection: "[data-samples-section]",
      samplesRoot: "[data-cart-samples]",
      packagingRoot: "[data-cart-packaging]",
      // Targets re-render via l'Ajax Section Rendering API.
      giftLine: "[data-gift-line]",
      headerCartLink: ".header__action-link--cart",
      popupAtcContainer: "[data-popup-atc-container]",
      cartItems: "[data-cart-items]",
      // Cartes du panier, ancrées à leur clé de ligne — voir `restoreCartOrder`.
      cartLine: "[data-cart-items] [data-line-key]",
      engravingPanelCart: ".engraving-panel-container-cart",
      cartSummary: "[data-cart-summary]",
      // Wrapper `#shopify-section-…` de la section cart-summary : présent même
      // quand le panier est vide (le contenu interne, lui, est conditionné par
      // `cart.item_count`), donc plus fiable que `[data-cart-summary]` pour
      // retrouver l'id de section.
      cartSummarySection: ".section-cart-summary"
    };
    // Initialisés ici et non dans le constructeur : `readDom()` les remplit au
    // premier usage, quand les enfants et les attributs existent enfin.
    this.form = null;
    this.countCartItems = null;
    this.formId = null;
    this.btnSubmit = null;
    this.btnSubmitAlreadyInfos = null;
    this.type = null;
    this.isCart = null;
    this.itemKey = null;
    this.action = "add";
    this.errorMessages = {};
    this.quantitySnapshot = null;
    this.samplesSectionId = null;
    this.sectionsRenderSamples = [];
    this.sectionsRender = [];
    this.hasConnected = false;
    this.submitForm = async () => {
      if (!this.form) return;
      this.clearCartError();
      const hiddenElements = Array.from(
        this.form.querySelectorAll("input, select, textarea")
      ).filter(
        (el) => el.offsetParent === null && el.type !== "hidden"
      );
      hiddenElements.forEach((el) => {
        el.disabled = true;
      });
      const formData = new FormData(this.form);
      hiddenElements.forEach((el) => {
        el.disabled = false;
      });
      const config = this.fetchConfig("javascript");
      const targets = this.withGiftLineTarget(
        this.isCart ? this.getCartPageTargets() : this.sectionsRender
      );
      const sectionsParam = targets.map((target) => target.id).join(",");
      formData.set("sections", sectionsParam);
      if (this.action === "change" && this.itemKey) {
        const properties = {};
        for (const [key, value] of formData.entries()) {
          if (key.includes("properties")) {
            const propKey = key.match(/\[(.*?)\]/g)?.pop()?.replace(/[[\]]/g, "");
            if (propKey) properties[propKey] = value;
          }
        }
        config.body = JSON.stringify({
          id: this.itemKey,
          properties,
          sections: sectionsParam
        });
      } else {
        config.body = formData;
        delete config.headers["Content-Type"];
      }
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      try {
        const endpoints = {
          add: "cart/add.js",
          change: "cart/change.js",
          update: "cart/update.js",
          remove: "cart/update.js",
          // Monter d'un cran est un simple ajout, que la Cart Transform fusionne
          // avec les lignes en place. Descendre n'a pas d'endpoint — voir
          // `setVirtualSetQuantity` juste en dessous.
          add_virtual_set: "cart/add.js"
        };
        const endpoint = endpoints[this.action] ?? endpoints["add"];
        if (this.action === "set_virtual_set") {
          await this.setVirtualSetQuantity(formData, sectionsParam, targets);
          return;
        }
        const response = await fetch(
          (window.Shopify?.routes?.root || "/") + endpoint,
          config
        );
        if (!response.ok) {
          throw await this.readCartError(
            response,
            this.action === "change" && this.itemKey ? [this.itemKey] : namedLineKeys(formData)
          );
        }
        const data = await response.json();
        hideCartLimitError();
        this.quantitySnapshot = null;
        const statusAnnouncement = this.buildCartMutationAnnouncement();
        await renderSections(targets, { sections: data.sections });
        if (statusAnnouncement) {
          announceCartStatus(statusAnnouncement);
        }
        this.dispatchEvent(
          new CustomEvent("cart:add-success", {
            bubbles: true,
            detail: { data, action: this.action }
          })
        );
        if (this.isCart) {
          this.renderSampleSection();
          await this.rerenderCartRecommendSections();
        }
      } catch (error) {
        const removal = this.isLineRemoval();
        const removalAnnouncement = removal ? this.buildCartMutationAnnouncement() : null;
        this.restoreQuantitySnapshot();
        console.error(error.message);
        if (this.isCart) await this.resyncCartPage(targets);
        if (!(error instanceof CartRequestError)) return;
        if (removal && this.isCart && !this.isLineShown(this.itemKey)) {
          if (removalAnnouncement) announceCartStatus(removalAnnouncement);
          return;
        }
        this.showCartError(error);
      }
    };
  }
  /**
   * Resolves the element's children and attributes, on demand.
   *
   * This used to run in the constructor, which is the one place it cannot: a
   * custom element constructor executes at `createElement` / at the opening tag,
   * **before any child or attribute exists**. The component only ever worked
   * because its definition happened to arrive *after* the subtree was complete,
   * so the upgrade replayed the constructor on a finished element — an accident
   * of load order, not a guarantee. The day an entry point imported this module
   * eagerly, the definition landed first and every field below came out null:
   * no submit listener, and quick-buy fell back to a native POST that navigated
   * to /cart instead of adding over AJAX.
   *
   * `connectedCallback` is no better on its own — for server-rendered HTML it
   * fires on the opening tag, also before the children. So the reads happen
   * here, called from the delegated handlers at interaction time, when the
   * subtree is complete whichever way it was built.
   *
   * Idempotent and cheap; re-resolving picks up a subtree the theme editor or a
   * section re-render swapped underneath us.
   */
  readDom() {
    this.form = this.querySelector(this.SELECTORS.form);
    this.countCartItems = this.dataset.countCartItems || null;
    this.formId = this.form?.id || null;
    this.btnSubmit = this.querySelector(this.SELECTORS.submitButton);
    this.btnSubmitAlreadyInfos = document.querySelector(
      `button[form="${this.formId}"]`
    );
    this.type = this.dataset.type || null;
    this.isCart = this.dataset.isCart || (document.body.classList.contains("body--cart") ? "true" : null);
    this.itemKey = this.dataset.itemKey || null;
    this.action = this.dataset.action || "add";
    this.errorMessages = {
      error_product_gift_cart: this.dataset.errorProductGiftCart || ""
    };
    this.samplesSectionId = this.getSamplesSectionId();
    this.sectionsRenderSamples = this.samplesSectionId ? [
      {
        id: this.samplesSectionId,
        selector: [
          this.SELECTORS.samplesRoot,
          this.SELECTORS.packagingRoot
        ]
      }
    ] : [];
    this.sectionsRender = [
      { id: "header", selector: [this.SELECTORS.headerCartLink] },
      { id: "popup-add-to-cart", selector: [this.SELECTORS.popupAtcContainer] }
    ];
  }
  getSamplesSectionId() {
    const sectionChild = document.querySelector(this.SELECTORS.samplesSection);
    return closestSectionId(sectionChild);
  }
  getCartSummarySectionId() {
    return closestSectionId(
      document.querySelector(this.SELECTORS.cartSummarySection)
    ) ?? closestSectionId(document.querySelector(this.SELECTORS.cartSummary));
  }
  // Page panier éclatée en plusieurs sections empilées. Les ids sont résolus au
  // moment du submit (et non dans le constructeur) : le wrapper cart-summary
  // peut être absent ou remplacé entre deux mutations du panier, et l'id réel
  // dépend de templates/cart.json (l'éditeur de thème peut le suffixer).
  getCartPageTargets() {
    const cartSummarySectionId = this.getCartSummarySectionId();
    return [
      { id: "header", selector: [this.SELECTORS.headerCartLink] },
      { id: "popup-add-to-cart", selector: [this.SELECTORS.popupAtcContainer] },
      {
        id: "main-cart",
        selector: [this.SELECTORS.cartItems, this.SELECTORS.engravingPanelCart]
      },
      // Outer swap (pas de `selector`) : le contenu de cart-summary est gated
      // par `cart.item_count`, un inner swap ne saurait pas passer de vide à
      // non-vide (et inversement).
      ...cartSummarySectionId ? [{ id: cartSummarySectionId }] : []
    ];
  }
  /**
   * Ajoute la ligne cadeau de CE formulaire aux cibles de re-rendu.
   *
   * Un produit ouvre droit à un cadeau, et le Liquid tranche au rendu — mais
   * après un ajout, seuls le header et le popup reviennent : le formulaire
   * resté à l'écran garderait ses inputs cadeau et en servirait un second au
   * clic suivant. On redemande donc au serveur la section qui le porte, et on
   * n'en réinjecte que le conteneur `[data-gift-line]` : re-rendre la section
   * entière écraserait la variante choisie et la quantité saisie.
   *
   * La section est résolue depuis le DOM plutôt que codée en dur, parce que le
   * formulaire vit dans une douzaine de sections différentes (PDP, cross-sell,
   * barre collante, cartes produit…). Une carte rendue côté client — l'îlot
   * Algolia — n'a pas de section porteuse : `closestSectionId` répond `null` et
   * rien n'est ajouté, l'îlot ayant sa propre lecture du panier.
   */
  withGiftLineTarget(targets) {
    if (!this.form?.querySelector(this.SELECTORS.giftLine)) return targets;
    const id = closestSectionId(this);
    if (!id) return targets;
    const existing = targets.find((target) => target.id === id);
    if (!existing) {
      return [...targets, { id, selector: [this.SELECTORS.giftLine] }];
    }
    if (!existing.selector) return targets;
    const selectors = Array.isArray(existing.selector) ? existing.selector : [existing.selector];
    if (selectors.includes(this.SELECTORS.giftLine)) return targets;
    return targets.map(
      (target) => target === existing ? { ...target, selector: [...selectors, this.SELECTORS.giftLine] } : target
    );
  }
  /**
   * Every listener is delegated on the host, not on `this.form`.
   *
   * `submit`, `change`, `click` and `focusin` all bubble, so binding on the
   * element itself works whether the children were parsed from HTML (they do
   * not exist yet at this point) or built by Preact. Binding on `this.form`
   * required the form to already exist, which is precisely what could not be
   * relied on — see `readDom()`.
   */
  connectedCallback() {
    if (this.hasConnected) return;
    this.hasConnected = true;
    setTimeout(() => {
      this.readDom();
      this.initNotifyMe();
      this.checkNotifyMeHash();
    }, 0);
    this.addEventListener("submit", async (e) => {
      this.readDom();
      if (!this.form) return;
      e.preventDefault();
      if (this.btnSubmit) this.btnSubmit.setAttribute("disabled", "true");
      if (this.btnSubmitAlreadyInfos)
        this.btnSubmitAlreadyInfos.setAttribute("disabled", "true");
      const loader = document.getElementById(this.SELECTORS.loaderId);
      if (loader) loader.setAttribute("aria-hidden", "false");
      await this.submitForm();
      if (loader) loader.setAttribute("aria-hidden", "true");
      if (this.btnSubmit) this.btnSubmit.removeAttribute("disabled");
      if (this.btnSubmitAlreadyInfos)
        this.btnSubmitAlreadyInfos.removeAttribute("disabled");
    });
    this.addEventListener("focusin", (e) => {
      this.readDom();
      if (this.action !== "update") return;
      const target = e.target;
      if (target.name?.startsWith("updates[")) this.captureQuantitySnapshot();
    });
    this.addEventListener("change", (e) => {
      const target = e.target;
      if (target.name?.includes("updates[")) {
        this.readDom();
        this.form?.requestSubmit();
      }
    });
    this.addEventListener("click", (e) => {
      const button = e.target.closest(
        this.SELECTORS.qtyButton
      );
      if (!button) return;
      this.readDom();
      if (!this.form) return;
      e.preventDefault();
      const step = parseInt(button.getAttribute("data-qty-step") || "0", 10);
      const inputs = Array.from(
        this.form.querySelectorAll(
          this.SELECTORS.updatesInput
        )
      );
      const stepped = inputs.filter(
        (el) => !el.matches(this.SELECTORS.qtyFixedInput)
      );
      const fixed = inputs.filter(
        (el) => el.matches(this.SELECTORS.qtyFixedInput)
      );
      let blockedByLimit = false;
      this.captureQuantitySnapshot();
      stepped.forEach((el) => {
        const currentVal = parseInt(el.value || "0", 10);
        const maxAttr = el.getAttribute("max");
        const maxQuantity = maxAttr ? parseInt(maxAttr, 10) : Number.NaN;
        const nextVal = currentVal + step;
        if (step > 0 && Number.isFinite(maxQuantity) && nextVal > maxQuantity) {
          blockedByLimit = true;
          return;
        }
        el.value = Math.max(0, nextVal).toString();
      });
      if (blockedByLimit) {
        this.quantitySnapshot = null;
        showCartLimitError();
        return;
      }
      const parentRemains = stepped.some(
        (el) => parseInt(el.value || "0", 10) > 0
      );
      fixed.forEach((el) => {
        el.value = parentRemains ? "1" : "0";
      });
      this.form.requestSubmit();
    });
  }
  /**
   * If the product is unavailable, the form renders an `<a data-notify-me-button>` link
   * instead of a submit button. This method polls for the third-party notify-me widget
   * and proxies clicks from our button to that widget.
   */
  initNotifyMe() {
    const notifyMeBtn = this.querySelector(this.SELECTORS.notifyMeButton);
    if (!notifyMeBtn) return;
    const href = notifyMeBtn.getAttribute("href") || "";
    if (href && !href.includes("#notify-me")) {
      notifyMeBtn.setAttribute("href", `${href}#notify-me`);
    }
    const bindNotifyWidget = (retries = 20, interval = 300) => {
      const widget = document.querySelector(
        this.SELECTORS.notifyWidget
      );
      if (widget) {
        widget.style.display = "none";
        notifyMeBtn.addEventListener("click", (event) => {
          event.preventDefault();
          widget.click();
        });
      } else if (retries > 0) {
        setTimeout(() => bindNotifyWidget(retries - 1, interval), interval);
      }
    };
    bindNotifyWidget();
  }
  /**
   * When landing on a product page with `#notify-me` in the URL (e.g. from a
   * collection card), auto-open the third-party notify-me widget.
   */
  checkNotifyMeHash() {
    if (window.location.hash !== "#notify-me") return;
    const autoOpenNotifyWidget = (retries = 20, interval = 300) => {
      const widget = document.querySelector(
        this.SELECTORS.notifyWidget
      );
      if (widget) {
        widget.click();
        history.replaceState(
          null,
          "",
          window.location.pathname + window.location.search
        );
      } else if (retries > 0) {
        setTimeout(() => autoOpenNotifyWidget(retries - 1, interval), interval);
      }
    };
    autoOpenNotifyWidget();
  }
  // Re-render la section `cart-samples` via l'Ajax Section Rendering API
  // (`?section_id=`) puis swap les blocs samples + packaging. Reload complet en
  // cas d'erreur.
  async renderSampleSection() {
    try {
      for (const section of this.sectionsRenderSamples) {
        await renderSection(section);
      }
    } catch (error) {
      console.error("[CartSamples] section render error:", error);
      window.location.reload();
    }
  }
  /**
   * Puts the cart page back in line with the cart after a failed mutation.
   *
   * The page can show a cart that has moved on — a line removed from another
   * tab, a page Safari brought back from its back/forward cache. Its inputs
   * then name line keys the cart no longer has, and `cart/update.js` refuses
   * the whole payload with a 422. Left on screen, those inputs were sent again
   * by every retry, which failed the same way. Same targets as a successful
   * mutation, fetched instead of read from the response.
   *
   * The recommendations are left alone: an Algolia round-trip and a carousel
   * back at its start, for no gain. A failed fetch keeps the page as it is —
   * no reload, unlike `renderSampleSection`: offline, that would trade the
   * cart for the browser's error page.
   */
  async resyncCartPage(targets) {
    try {
      await renderSections(targets);
      for (const section of this.sectionsRenderSamples) {
        await renderSection(section);
      }
    } catch (error) {
      console.error("[Cart] resync error:", error);
    }
  }
  /** Whether this submit takes its line out of the cart: the ✕, or `−` to 0. */
  isLineRemoval() {
    if (this.action === "remove") return true;
    return this.action === "update" && this.readAnnouncedQuantity() === 0;
  }
  /** Whether the cart page shows the line of this key. True without a key. */
  isLineShown(key) {
    if (!key) return true;
    return Boolean(
      document.querySelector(
        `${this.SELECTORS.cartItems} [data-line-key="${CSS.escape(key)}"]`
      )
    );
  }
  /**
   * Whether the refused request named a line the cart no longer has: the page
   * was behind the cart, and Shopify refused the whole payload for it — in the
   * shop's language, so the key tells, not the wording. Every line but the
   * samples shows its key, gift lines included, and no form of this element
   * names a sample. Read once the page is back in line with the cart: a failed
   * resync leaves every line shown.
   */
  isStale(error) {
    if (!this.isCart) return false;
    return error.lineKeys.some((key) => !this.isLineShown(key));
  }
  /**
   * Pose le nombre de coffrets voulu : on vide la ligne, puis on la repose.
   *
   * Aucun endpoint de l'Ajax Cart API ne sait retrancher sur une ligne
   * fusionnée. Elle rapporte toujours `quantity: 1`, donc tout ce qu'on lui
   * écrit est consommé comme facteur : depuis n coffrets on n'atteint que n×k
   * ou n+m, et `n−1` est hors du domaine. Vérifié levier par levier en
   * boutique, y compris les clés de `item_components`, que Liquid rend mais que
   * `updates` refuse en 422.
   *
   * Le formulaire porte les deux moitiés côte à côte — le `updates[…] = 0` qui
   * vide la ligne, et les `items[…]` qui la reposent à la cible. On les sépare
   * ici parce qu'elles ne partent pas au même endpoint.
   *
   * L'ordre n'est pas négociable : reposer avant de vider ferait fusionner le
   * nouvel envoi avec l'ancien, et doublerait le coffret. La contrepartie est
   * assumée — si le second appel échoue, le coffret est déjà sorti du panier —
   * et n'a pas d'alternative tant que la Cart Transform fixe la quantité à 1.
   *
   * Seule la réponse du réajout porte les sections : c'est l'état final. Rendre
   * celles de la suppression ferait clignoter un panier sans le coffret.
   */
  async setVirtualSetQuantity(formData, sectionsParam, targets) {
    const removals = new FormData();
    const additions = new FormData();
    for (const [key, value] of formData.entries()) {
      if (key.startsWith("updates[")) {
        removals.append(key, value);
      } else if (key.startsWith("items[")) {
        additions.append(key, value);
      }
    }
    if (![...additions.keys()].length) {
      throw new Error("[VirtualSet] aucun items[\u2026] \xE0 reposer");
    }
    additions.set("sections", sectionsParam);
    const order = this.readCartOrder();
    const root = window.Shopify?.routes?.root || "/";
    const headers = { "X-Requested-With": "XMLHttpRequest" };
    if ([...removals.keys()].length) {
      const removed = await fetch(`${root}cart/update.js`, {
        method: "POST",
        headers,
        body: removals
      });
      if (!removed.ok) {
        throw await this.readCartError(removed, namedLineKeys(removals));
      }
    }
    const added = await fetch(`${root}cart/add.js`, {
      method: "POST",
      headers,
      body: additions
    });
    if (!added.ok) throw await this.readCartError(added);
    const data = await added.json();
    hideCartLimitError();
    this.quantitySnapshot = null;
    const statusAnnouncement = this.buildCartMutationAnnouncement();
    await renderSections(targets, { sections: data.sections });
    this.restoreCartOrder(order);
    if (statusAnnouncement) {
      announceCartStatus(statusAnnouncement);
    }
    this.dispatchEvent(
      new CustomEvent("cart:add-success", {
        bubbles: true,
        detail: { data, action: this.action }
      })
    );
    if (this.isCart) {
      this.renderSampleSection();
      await this.rerenderCartRecommendSections();
    }
  }
  /** Les clés de ligne des cartes du panier, dans l'ordre où elles s'affichent. */
  readCartOrder() {
    return [
      ...document.querySelectorAll(this.SELECTORS.cartLine)
    ].flatMap((card) => card.dataset.lineKey ? [card.dataset.lineKey] : []);
  }
  /**
   * Remet les cartes du panier dans l'ordre relevé avant l'opération.
   *
   * Second filet, et non doublon de l'épinglage que fait `main-cart` : celui-ci
   * fixe la position de la FAMILLE — les coffrets en tête, l'emballage cadeau
   * en queue — mais laisse les coffrets entre eux dans l'ordre de `cart.items`.
   * Or descendre la quantité de l'un impose de vider sa ligne pour la reposer,
   * ce qui l'envoie en fin de tableau : avec deux coffrets au panier, agir sur
   * le premier le ferait passer derrière le second.
   *
   * Rejouer l'ordre relevé au clic répare ce cas-là, que l'épinglage ne couvre
   * pas. Réordonner et non figer : personne n'est assigné à un rang, et les
   * cartes absentes du relevé — une ligne apparue entre-temps — restent après,
   * dans leur ordre courant.
   *
   * Ne survit pas à un rechargement, où l'ordre de `cart.items` reprend la
   * main. Le stabiliser demanderait de mémoriser l'ordre côté serveur.
   *
   * La clé de ligne survit au vidage-réajout : vérifié en boutique, elle est
   * identique avant et après, ce qui est ce qui rend l'appariement possible.
   */
  restoreCartOrder(order) {
    if (order.length < 2) return;
    const cards = [
      ...document.querySelectorAll(this.SELECTORS.cartLine)
    ];
    const parent = cards[0]?.parentElement;
    if (!parent) return;
    const byKey = new Map(
      cards.filter((card) => card.parentElement === parent && card.dataset.lineKey).map((card) => [card.dataset.lineKey, card])
    );
    for (const key of order) {
      const card = byKey.get(key);
      if (card) {
        parent.appendChild(card);
        byKey.delete(key);
      }
    }
    for (const card of byKey.values()) parent.appendChild(card);
  }
  /**
   * Reads a failed Cart API answer. Shown by `submitForm`, once the cart page
   * is back in line with the cart: by then the error may have nothing left to
   * report. `lineKeys`: the cart lines the request named.
   */
  async readCartError(response, lineKeys = []) {
    const errorData = await response.json().catch(() => ({}));
    const text = errorData.description || errorData.message;
    return new CartRequestError(
      response.status,
      !text || TECHNICAL_ERROR_PATTERN.test(text),
      lineKeys,
      text || `Erreur ${response.status}`
    );
  }
  /** The banner of a failed Cart API answer — never Shopify's technical wording. */
  showCartError(error) {
    const rawMessage = error.message;
    if (isCartQuantityLimitError(error.status, rawMessage) || STOCK_LIMIT_PATTERN.test(rawMessage)) {
      showCartLimitError();
      return;
    }
    const fallback = error.technical || this.isStale(error) ? this.dataset.errorMessage || DEFAULT_CART_ERROR : rawMessage;
    showCartLimitError(this.errorMessages[rawMessage] || fallback);
  }
  captureQuantitySnapshot() {
    if (!this.form || this.action !== "update") return;
    this.quantitySnapshot = /* @__PURE__ */ new Map();
    this.form.querySelectorAll(this.SELECTORS.updatesInput).forEach((input) => {
      this.quantitySnapshot.set(input.name, input.value);
    });
  }
  restoreQuantitySnapshot() {
    if (!this.form || !this.quantitySnapshot) return;
    this.form.querySelectorAll(this.SELECTORS.updatesInput).forEach((input) => {
      const saved = this.quantitySnapshot.get(input.name);
      if (saved !== void 0) input.value = saved;
    });
    this.quantitySnapshot = null;
  }
  clearCartError() {
    hideCartLimitError();
  }
  /**
   * Builds a screen-reader message for a successful cart mutation, or null when
   * another surface (ATC popup) already announces the result.
   */
  buildCartMutationAnnouncement() {
    const title = this.dataset.productTitle?.trim() || "Item";
    if (this.action === "remove") {
      const template = this.dataset.itemRemovedTemplate || "__TITLE__ removed from bag";
      return fillCartStatusTemplate(template, { title });
    }
    if (this.action === "update" || this.action === "set_virtual_set" || this.action === "add_virtual_set" && this.isCart) {
      const quantity = this.readAnnouncedQuantity();
      if (quantity === 0) {
        const removedTemplate = this.dataset.itemRemovedTemplate || "__TITLE__ removed from bag";
        return fillCartStatusTemplate(removedTemplate, { title });
      }
      if (quantity !== null) {
        const template = this.dataset.quantityUpdatedTemplate || "__TITLE__ quantity updated to __QUANTITY__";
        return fillCartStatusTemplate(template, { title, quantity });
      }
    }
    if ((this.action === "add" || this.action === "add_virtual_set") && this.isCart) {
      const template = this.dataset.itemAddedTemplate || "__TITLE__ was added to bag";
      return fillCartStatusTemplate(template, { title });
    }
    return null;
  }
  /**
   * Resolves the quantity to announce after a cart stepper mutation.
   */
  readAnnouncedQuantity() {
    if (!this.form) return null;
    if (this.action === "set_virtual_set") {
      const quantityInput = this.form.querySelector(
        'input[name^="items["][name$="[quantity]"]'
      );
      if (!quantityInput) return null;
      const value2 = parseInt(quantityInput.value, 10);
      return Number.isFinite(value2) ? value2 : null;
    }
    if (this.action === "add_virtual_set") {
      const lineRoot = this.closest("[data-line-key]");
      const displayInput = lineRoot?.querySelector(
        `${this.SELECTORS.updatesInput}:not(${this.SELECTORS.qtyFixedInput})`
      );
      if (!displayInput) return null;
      const current = parseInt(displayInput.value, 10);
      if (!Number.isFinite(current)) return null;
      return current + 1;
    }
    const updatesInput = this.form.querySelector(
      `${this.SELECTORS.updatesInput}:not(${this.SELECTORS.qtyFixedInput})`
    );
    if (!updatesInput) return null;
    const value = parseInt(updatesInput.value, 10);
    return Number.isFinite(value) ? value : null;
  }
  fetchConfig(type = "json") {
    return {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: `application/${type}`
      }
    };
  }
  // Outer swap des sections de recommandations : on collecte les ids des
  // wrappers `#shopify-section-…` autour de chaque `[data-cart-recommend-root]`
  // puis on remplace chaque wrapper par sa version fraîche.
  async rerenderCartRecommendSections() {
    const roots = document.querySelectorAll(this.SELECTORS.cartRecommendRoot);
    const sectionIds = collectSectionIds(roots);
    if (sectionIds.length === 0) return;
    await renderSections(sectionIds.map((id) => ({ id })));
  }
  disconnectedCallback() {
  }
};
if (!customElements.get("c-product-form")) {
  customElements.define("c-product-form", CProductForm);
  window.addEventListener("pageshow", (event) => {
    if (event.persisted && document.body.classList.contains("body--cart")) {
      window.location.reload();
    }
  });
  const [navigation] = performance.getEntriesByType(
    "navigation"
  );
  if (navigation?.type === "back_forward" && !(navigation.transferSize > 0) && document.body.classList.contains("body--cart")) {
    window.location.reload();
  }
}
initAlgoliaInsights();

// src/blocks/_algolia-recommend-list/shared.ts
function parseJsonObject(value) {
  if (!value?.trim()) return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch (error) {
    console.warn("Invalid Algolia Recommend JSON parameter", error);
    return {};
  }
}
function parseJsonArray(value) {
  if (!value?.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.map((item) => String(item).trim()).filter(Boolean) : [];
  } catch (error) {
    console.warn("Invalid Algolia Recommend JSON array", error);
    return [];
  }
}
function parseCartItems(value) {
  if (!value?.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter(
      (item) => item !== null && typeof item === "object"
    ) : [];
  } catch (error) {
    console.warn("Invalid Algolia Recommend cart items JSON", error);
    return [];
  }
}
function parseStringList(value) {
  if (!value?.trim()) return [];
  const trimmed = value.trim();
  if (trimmed.startsWith("[")) return parseJsonArray(trimmed);
  return trimmed.split(/[\n,]/).map((item) => item.trim()).filter(Boolean);
}
function sanitizeRuleContexts(contexts) {
  return contexts.map((context) => context.replace(/[^A-Za-z0-9_-]/g, "")).filter(Boolean).slice(0, 10);
}
function boundedNumber(value, fallback, min, max) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(Math.max(parsed, min), max);
}
function isObjectModel(model) {
  return ["bought-together", "looking-similar", "related-products"].includes(
    model
  );
}
function isTrendingModel(model) {
  return model === "trending-items";
}
function normalizeAlgoliaFilterSyntax(value) {
  return value.replace(/"([A-Za-z0-9_.-]+)"\s*:\s*/g, "$1:");
}
function applyFacetPlaceholder(value, facetReplacementValue) {
  if (!facetReplacementValue) return value;
  return value.replaceAll("REPLACE_WITH_FACET_ATTRIBUTE", facetReplacementValue);
}
function resolveFacetCollectionValue(facetName, collectionHandle, collectionId) {
  switch (facetName?.trim()) {
    case "collections":
      return collectionHandle?.trim() || void 0;
    case "collection_ids":
      return collectionId?.trim() || void 0;
    default:
      return void 0;
  }
}
function resolveFilterString(value, facetReplacementValue) {
  const cleanValue = value?.trim();
  if (!cleanValue) return "";
  return normalizeAlgoliaFilterSyntax(
    applyFacetPlaceholder(cleanValue, facetReplacementValue)
  );
}
function prepareFilterParameters(parameters, facetReplacementValue) {
  const nextParameters = { ...parameters };
  if (typeof nextParameters.filters === "string") {
    nextParameters.filters = resolveFilterString(
      nextParameters.filters,
      facetReplacementValue
    );
  }
  if (typeof nextParameters.query === "string") {
    nextParameters.query = applyFacetPlaceholder(
      nextParameters.query,
      facetReplacementValue
    );
  }
  return nextParameters;
}
function mergeQueryFilters(queryParameters, filters) {
  const nextQueryParameters = { ...queryParameters };
  const cleanFilters = filters?.trim();
  if (cleanFilters) appendFilter(nextQueryParameters, cleanFilters);
  return nextQueryParameters;
}
function appendFilter(queryParameters, filter) {
  const currentFilters = typeof queryParameters.filters === "string" ? queryParameters.filters.trim() : "";
  queryParameters.filters = currentFilters ? `(${currentFilters}) AND (${filter})` : filter;
}
function quoteFilterValue(value) {
  return `"${value.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}
function collectionFilter(attribute, value) {
  const cleanAttribute = attribute?.trim();
  const cleanValue = value?.trim();
  if (!cleanAttribute || !cleanValue) return "";
  return `${cleanAttribute}:${quoteFilterValue(cleanValue)}`;
}
function buildRequestQueryParameters(config) {
  const facetReplacementValue = resolveFacetCollectionValue(
    config.facetName,
    config.collectionHandle,
    config.collectionId
  ) || config.facetValue?.trim() || config.collectionFilterValue?.trim() || void 0;
  const queryParameters = prepareFilterParameters(
    config.queryParameters,
    facetReplacementValue
  );
  const mergedQueryParameters = mergeQueryFilters(
    queryParameters,
    resolveFilterString(config.queryFilters, facetReplacementValue)
  );
  const resolvedCollectionFilter = collectionFilter(
    config.collectionFilterAttribute,
    config.collectionFilterValue
  );
  if (resolvedCollectionFilter && !isTrendingModel(config.model)) {
    appendFilter(mergedQueryParameters, resolvedCollectionFilter);
  }
  const fallbackParameters = prepareFilterParameters(
    config.fallbackParameters,
    facetReplacementValue
  );
  appendFilter(mergedQueryParameters, ALGOLIA_HIDDEN_PRODUCTS_FILTER);
  appendFilter(fallbackParameters, ALGOLIA_HIDDEN_PRODUCTS_FILTER);
  return {
    facetReplacementValue,
    fallbackParameters,
    queryParameters: mergedQueryParameters,
    resolvedCollectionFilter
  };
}
function buildBaseRecommendRequest(config) {
  const {
    facetReplacementValue,
    fallbackParameters,
    queryParameters,
    resolvedCollectionFilter
  } = buildRequestQueryParameters(config);
  const ruleContexts = [
    ...sanitizeRuleContexts(config.ruleContexts ?? []),
    ALGOLIA_HIDDEN_GUARD_CONTEXT
  ];
  const request = {
    indexName: config.indexName,
    maxRecommendations: config.limit,
    model: config.model,
    queryParameters: {
      attributesToRetrieve: productAttributesToRetrieve(),
      responseFields: ALGOLIA_PRODUCT_RESPONSE_FIELDS,
      // Enables context-scoped Recommend rules and segments the placement in
      // Algolia's own search analytics. Kept before the spread so a block's
      // `queryParameters` JSON can still override them explicitly.
      ...config.analyticsTags?.length ? { analyticsTags: config.analyticsTags } : {},
      ruleContexts,
      ...queryParameters
    },
    threshold: config.threshold
  };
  if (isTrendingModel(config.model)) {
    const facetName = config.facetName?.trim() || (resolvedCollectionFilter ? config.collectionFilterAttribute?.trim() : "");
    const facetValue = applyFacetPlaceholder(
      config.facetValue?.trim() || (resolvedCollectionFilter ? config.collectionFilterValue?.trim() ?? "" : "") || "",
      facetReplacementValue
    );
    if (facetName && facetValue) {
      request.facetName = facetName;
      request.facetValue = facetValue;
    }
  }
  if (Object.keys(fallbackParameters).length > 0) {
    request.fallbackParameters = fallbackParameters;
  }
  return request;
}
async function readAlgoliaError(response) {
  try {
    const data = await response.json();
    return data.message ? `: ${data.message}` : "";
  } catch {
    return "";
  }
}
function sameString(firstValue, secondValue) {
  const first = String(firstValue ?? "").trim();
  const second = String(secondValue ?? "").trim();
  return first !== "" && second !== "" && first === second;
}
function hitMatchesCartItem(hit, item) {
  return sameString(hit.handle, item.handle) || sameString(hit.id, item.product_id) || sameString(hit.product_id, item.product_id) || sameString(hit.variant_id, item.variant_id) || sameString(hit.sku, item.sku);
}
function searchParamsForCartItem(item) {
  const params = new URLSearchParams();
  const query = String(item.handle ?? "").trim() || String(item.sku ?? "").trim() || String(item.product_id ?? "").trim() || String(item.variant_id ?? "").trim();
  params.set("query", query);
  params.set("hitsPerPage", "10");
  params.set("attributesToHighlight", "[]");
  params.set(
    "attributesToRetrieve",
    JSON.stringify([
      "objectID",
      "id",
      "product_id",
      "variant_id",
      "sku",
      "handle"
    ])
  );
  return params.toString();
}
var AlgoliaRecommendBase = class extends GalleryListBase {
  constructor() {
    super(...arguments);
    this.abortController = null;
    this.isSliderReady = false;
  }
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    void this.loadRecommendations();
  }
  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
    super.disconnectedCallback();
  }
  async fetchRecommendations(appId, apiKey, requests) {
    const resultGroups = await this.fetchRecommendationResultGroups(
      appId,
      apiKey,
      requests
    );
    return resultGroups.flat();
  }
  async fetchRecommendationResultGroups(appId, apiKey, requests) {
    const response = await fetch(
      `https://${appId.toLowerCase()}-dsn.algolia.net/1/indexes/*/recommendations`,
      {
        body: JSON.stringify({ requests }),
        headers: {
          accept: "application/json",
          "content-type": "application/json",
          "x-algolia-api-key": apiKey,
          "x-algolia-application-id": appId
        },
        method: "POST",
        signal: this.abortController?.signal
      }
    );
    if (!response.ok) {
      const message = await readAlgoliaError(response);
      throw new Error(`Algolia Recommend failed: ${response.status}${message}`);
    }
    const data = await response.json();
    return data.results?.map((result) => result.hits ?? []) ?? [];
  }
  async fetchSearchResponse(appId, apiKey, indexName, cartItems) {
    const response = await fetch(
      `https://${appId.toLowerCase()}-dsn.algolia.net/1/indexes/*/queries`,
      {
        body: JSON.stringify({
          requests: cartItems.map((item) => ({
            indexName,
            params: searchParamsForCartItem(item)
          }))
        }),
        headers: {
          accept: "application/json",
          "content-type": "application/json",
          "x-algolia-api-key": apiKey,
          "x-algolia-application-id": appId
        },
        method: "POST",
        signal: this.abortController?.signal
      }
    );
    if (!response.ok) {
      const message = await readAlgoliaError(response);
      throw new Error(
        `Algolia cart objectID lookup failed: ${response.status}${message}`
      );
    }
    return await response.json();
  }
  mergeHits(hits, excludeObjectIDs, limit) {
    const seenObjectIDs = /* @__PURE__ */ new Set();
    const mergedHits = [];
    for (const hit of hits) {
      const objectID = String(hit.objectID ?? "").trim();
      if (!objectID) continue;
      if (excludeObjectIDs.has(objectID) || seenObjectIDs.has(objectID))
        continue;
      seenObjectIDs.add(objectID);
      mergedHits.push(hit);
      if (mergedHits.length >= limit) break;
    }
    return mergedHits;
  }
  mergeHitGroups(hitGroups, excludeObjectIDs, limit) {
    const seenObjectIDs = /* @__PURE__ */ new Set();
    const mergedHits = [];
    const indexes = hitGroups.map(() => 0);
    while (mergedHits.length < limit) {
      let hasRemainingHits = false;
      for (let groupIndex = 0; groupIndex < hitGroups.length; groupIndex += 1) {
        const group = hitGroups[groupIndex];
        while (indexes[groupIndex] < group.length) {
          const hit = group[indexes[groupIndex]];
          indexes[groupIndex] += 1;
          hasRemainingHits = true;
          const objectID = String(hit.objectID ?? "").trim();
          if (!objectID) continue;
          if (excludeObjectIDs.has(objectID) || seenObjectIDs.has(objectID))
            continue;
          seenObjectIDs.add(objectID);
          mergedHits.push(hit);
          break;
        }
        if (mergedHits.length >= limit) break;
      }
      if (!hasRemainingHits) break;
    }
    return mergedHits;
  }
  async renderHits(hits, excludeObjectIDs, limit) {
    const mergedHits = this.mergeHits(hits, excludeObjectIDs, limit);
    await this.waitForSliderDependencies();
    this.render(mergedHits);
  }
  async renderHitGroups(hitGroups, excludeObjectIDs, limit) {
    const mergedHits = this.mergeHitGroups(hitGroups, excludeObjectIDs, limit);
    await this.waitForSliderDependencies();
    this.render(mergedHits);
  }
  withBusyState(isBusy) {
    if (isBusy) {
      this.setAttribute("aria-busy", "true");
    } else {
      this.removeAttribute("aria-busy");
    }
  }
  handleLoadError(error) {
    if (error.name === "AbortError") return;
    console.error(error);
    this.setEmptyState();
  }
  setEmptyState() {
    const container = this.querySelector("[data-algolia-recommend-items]");
    if (container) container.innerHTML = "";
    if (this.dataset.hideWhenEmpty === "false") {
      this.hidden = false;
    } else {
      this.hidden = true;
    }
  }
  hasSliderDependencies() {
    return "EmblaCarousel" in window && "EmblaCarouselAutoHeight" in window;
  }
  async waitForSliderDependencies() {
    if (this.hasSliderDependencies()) return;
    await new Promise((resolve) => {
      const timeout = window.setTimeout(resolve, 3e3);
      window.addEventListener(
        "load",
        () => {
          window.clearTimeout(timeout);
          resolve();
        },
        { once: true }
      );
    });
    if (!this.hasSliderDependencies()) {
      throw new Error("Embla carousel dependencies are not available");
    }
  }
  readProductCardAssets() {
    return {
      alertIcon: this.querySelector(
        'template[data-algolia-icon="alert"]'
      )?.innerHTML.trim() ?? "",
      notifyMeLabel: this.dataset.notifyMeLabel || "",
      quickBuyIcon: this.querySelector(
        'template[data-algolia-icon="quick-buy"]'
      )?.innerHTML.trim() ?? "",
      quickBuyLabel: this.dataset.quickBuyLabel || "",
      setToComposeLabel: this.dataset.setToComposeLabel || "",
      cardActionAriaLabel: this.dataset.cardActionAriaLabel || "",
      contenanceLabel: this.dataset.contenanceLabel || "Contenance",
      modelsLabelOne: this.dataset.modelsLabelOne || "+{{ count }} model",
      modelsLabelOther: this.dataset.modelsLabelOther || "+{{ count }} models"
    };
  }
  render(hits) {
    const container = this.querySelector("[data-algolia-recommend-items]");
    if (!container) return;
    if (hits.length === 0) {
      this.setEmptyState();
      return;
    }
    const assets = this.readProductCardAssets();
    const paint = (gifts) => renderCards(container, {
      assets,
      gifts,
      hits,
      slideClass: "container-product-card embla__slide"
    });
    paint(null);
    void Promise.all([resolveGiftsForHits(hits), refreshGiftedHandles()]).then(
      ([gifts]) => {
        if (!container.isConnected) return;
        const dejaGratifie = hits.some((hit) => hasGiftInCart(hit.handle));
        if (gifts || dejaGratifie) paint(gifts);
      }
    );
    this.style.setProperty("--nbr-slides", String(hits.length));
    this.toggleAttribute("data-hide-progress-bar-desktop", hits.length < 5);
    this.hidden = false;
    if (!this.isSliderReady) {
      this.isSliderReady = true;
      super.connectedCallback();
    }
  }
};

// src/blocks/_algolia-recommend-list/product-recommend.ts
function readProductRecommendConfig(dataset) {
  const {
    algoliaApiKey: apiKey,
    algoliaAppId: appId,
    algoliaIndexName: indexName,
    analyticsTags,
    collectionFilterAttribute,
    collectionFilterValue,
    collectionHandle,
    collectionId,
    fallbackParameters,
    facetName,
    facetValue,
    limit,
    objectId,
    queryFilters,
    queryParameters,
    recommendModel,
    ruleContexts,
    threshold
  } = dataset;
  const model = recommendModel;
  if (!apiKey || !appId || !indexName || !model) return null;
  const analyticsTagList = parseStringList(analyticsTags);
  const ruleContextList = parseStringList(ruleContexts);
  return {
    analyticsTags: analyticsTagList.length ? analyticsTagList : ["recommend-product"],
    apiKey,
    appId,
    collectionFilterAttribute,
    collectionFilterValue,
    collectionHandle,
    collectionId,
    fallbackParameters: parseJsonObject(fallbackParameters),
    facetName,
    facetValue,
    indexName,
    limit: boundedNumber(limit, 8, 1, 30),
    model,
    objectID: String(objectId ?? "").trim() || void 0,
    queryFilters,
    queryParameters: parseJsonObject(queryParameters),
    ruleContexts: ruleContextList.length ? ruleContextList : ["pdp"],
    threshold: boundedNumber(threshold, 0, 0, 100)
  };
}
var AlgoliaProductRecommendList = class extends AlgoliaRecommendBase {
  async loadRecommendations() {
    const config = readProductRecommendConfig(this.dataset);
    if (!config) {
      this.setEmptyState();
      return;
    }
    if (isObjectModel(config.model) && !config.objectID) {
      this.setEmptyState();
      return;
    }
    const baseRequest = buildBaseRecommendRequest(config);
    const requests = isObjectModel(config.model) && config.objectID ? [{ ...baseRequest, objectID: config.objectID }] : [baseRequest];
    this.withBusyState(true);
    try {
      const hits = await this.fetchRecommendations(
        config.appId,
        config.apiKey,
        requests
      );
      await this.renderHits(hits, /* @__PURE__ */ new Set(), config.limit);
    } catch (error) {
      this.handleLoadError(error);
    } finally {
      this.withBusyState(false);
    }
  }
};
if (!window.customElements.get("c-algolia-product-recommend-list")) {
  window.customElements.define(
    "c-algolia-product-recommend-list",
    AlgoliaProductRecommendList
  );
}

// src/blocks/_algolia-recommend-list/cart-recommend.ts
function readCartRecommendConfig(dataset) {
  const {
    algoliaApiKey: apiKey,
    algoliaAppId: appId,
    algoliaIndexName: indexName,
    analyticsTags,
    cartItems,
    cartObjectIds,
    collectionFilterAttribute,
    collectionFilterValue,
    collectionHandle,
    collectionId,
    fallbackParameters,
    facetName,
    facetValue,
    limit,
    queryFilters,
    queryParameters,
    recommendModel,
    ruleContexts,
    threshold
  } = dataset;
  const model = recommendModel;
  if (!apiKey || !appId || !indexName || !model) return null;
  const analyticsTagList = parseStringList(analyticsTags);
  const ruleContextList = parseStringList(ruleContexts);
  return {
    analyticsTags: analyticsTagList.length ? analyticsTagList : ["recommend-cart"],
    apiKey,
    appId,
    cartItems: parseCartItems(cartItems).slice(0, 20),
    cartObjectIDs: parseJsonArray(cartObjectIds).slice(0, 20),
    collectionFilterAttribute,
    collectionFilterValue,
    collectionHandle,
    collectionId,
    fallbackParameters: parseJsonObject(fallbackParameters),
    facetName,
    facetValue,
    indexName,
    limit: boundedNumber(limit, 8, 1, 30),
    model,
    queryFilters,
    queryParameters: parseJsonObject(queryParameters),
    ruleContexts: ruleContextList.length ? ruleContextList : ["cart"],
    threshold: boundedNumber(threshold, 0, 0, 100)
  };
}
function buildObjectRequests(baseRequest, objectIDs) {
  return objectIDs.map((objectID) => ({ ...baseRequest, objectID }));
}
var AlgoliaCartRecommendList = class extends AlgoliaRecommendBase {
  async loadRecommendations() {
    const config = readCartRecommendConfig(this.dataset);
    if (!config) {
      this.setEmptyState();
      return;
    }
    this.withBusyState(true);
    try {
      const baseRequest = buildBaseRecommendRequest(config);
      const objectIDs = isObjectModel(config.model) ? await this.resolveCartObjectIDs(config) : [];
      if (isObjectModel(config.model) && objectIDs.length === 0) {
        this.setEmptyState();
        return;
      }
      const requests = isObjectModel(config.model) ? buildObjectRequests(baseRequest, objectIDs) : [baseRequest];
      const excludeObjectIDs = new Set(objectIDs);
      if (isObjectModel(config.model)) {
        const hitGroups = await this.fetchRecommendationResultGroups(
          config.appId,
          config.apiKey,
          requests
        );
        await this.renderHitGroups(hitGroups, excludeObjectIDs, config.limit);
      } else {
        const hits = await this.fetchRecommendations(
          config.appId,
          config.apiKey,
          requests
        );
        await this.renderHits(hits, excludeObjectIDs, config.limit);
      }
    } catch (error) {
      this.handleLoadError(error);
    } finally {
      this.withBusyState(false);
    }
  }
  async resolveCartObjectIDs(config) {
    if (config.cartItems.length === 0) {
      return config.cartObjectIDs;
    }
    const data = await this.fetchSearchResponse(
      config.appId,
      config.apiKey,
      config.indexName,
      config.cartItems
    );
    const resolvedObjectIDs = data.results?.map((result, index) => {
      const item = config.cartItems[index];
      const matchingHit = result.hits?.find((hit) => hitMatchesCartItem(hit, item)) ?? result.hits?.[0];
      return String(matchingHit?.objectID ?? "").trim();
    }).filter(Boolean) ?? [];
    return [...new Set(resolvedObjectIDs)];
  }
};
if (!window.customElements.get("c-algolia-cart-recommend-list")) {
  window.customElements.define(
    "c-algolia-cart-recommend-list",
    AlgoliaCartRecommendList
  );
}
